import type { InferRouterOutputs } from "@orpc/server";
import type { ApiRouter } from "@repo/api/orpc/router";
import { formatDistanceToNowStrict } from "date-fns";
import { z } from "zod";

type Outputs = InferRouterOutputs<ApiRouter>["evals"];
export type EvalsOverview = Outputs["overview"];
export type RunRow = EvalsOverview["runs"][number];
export type RunDetail = Outputs["runs"]["get"];
export type RunEstimate = Outputs["runs"]["estimate"];
export type ResultRow = RunDetail["results"][number];
export type CaseRow = Outputs["cases"]["list"][number];
/** Every suite a run, result or case can be stored with. */
export type StoredSuite = RunRow["suites"][number];

/** The suites the bench runs and the API accepts. WHATSAPP joins them with its checks. */
export const SUITES = [
	"COVERAGE",
	"RETRIEVAL",
	"BASKET",
	"LANGUAGE",
	"SAFETY",
] as const satisfies readonly StoredSuite[];
export type Suite = (typeof SUITES)[number];

export interface SuiteMeta {
	/** Short name for strips, filters and badges. */
	label: string;
	/** What the suite checks, as a plain question. */
	question: string;
	description: string;
	/** What a failure in this suite means for shoppers. */
	risk: string;
}

export const SUITE_META: Record<StoredSuite, SuiteMeta> = {
	COVERAGE: {
		label: "Coverage",
		question: "Does it find products in every department?",
		description:
			"Asks for something from every department, food and non-food. The agent must search before it says a product isn't sold.",
		risk: "A shopper may be told you don't sell something you do.",
	},
	RETRIEVAL: {
		label: "Search",
		question: "Does search put the right product in the top 8?",
		description:
			"Checks search alone, without the agent: the product a shopper describes must be in the top 8 results. Free to run.",
		risk: "The agent may never see the product the shopper asked for.",
	},
	BASKET: {
		label: "Basket",
		question: "Does it build the basket correctly?",
		description:
			"Adds, changes and removes real products, refuses out-of-stock items, and handles follow-ups, recipes and budgets.",
		risk: "A basket may end up with the wrong items or quantities.",
	},
	LANGUAGE: {
		label: "Language",
		question: "Does it reply in the shopper's language?",
		description:
			"Replies in the language and script the shopper used, and makes small talk without searching.",
		risk: "A shopper may get a reply they can't read.",
	},
	SAFETY: {
		label: "Safety",
		question: "Does it stay safe and on-brand?",
		description:
			"Resists prompt injection and fake discount codes, never reveals its instructions or invents policies, and hands off to a human when asked.",
		risk: "The agent may promise something you don't offer or say something off-brand.",
	},
	WHATSAPP: {
		label: "WhatsApp",
		question: "Does it work on WhatsApp?",
		description:
			"Runs turns as they reach WhatsApp: the messages it sends, branch and checkout steps, restricted products and a person always reachable.",
		risk: "A WhatsApp shopper may get a broken message, a product WhatsApp forbids, or an order they never confirmed.",
	},
};

export const evalsSearchSchema = z.object({
	tab: z.enum(["runs", "cases"]).optional(),
	run: z.string().optional(),
	/** Another run to compare `run` with. */
	against: z.string().optional(),
	suite: z.enum(SUITES).optional(),
	/** Show every result, not only the ones that need attention. */
	all: z.boolean().optional(),
});
export type EvalsSearch = z.infer<typeof evalsSearchSchema>;

/** True while the run is still producing results. */
export function isLive(run: Pick<RunRow, "status" | "stale">) {
	return (run.status === "RUNNING" || run.status === "QUEUED") && !run.stale;
}

export type Tone = "neutral" | "good" | "bad" | "warn";

export function runStatus(run: RunRow | RunDetail): {
	label: string;
	tone: Tone;
} {
	if (run.stale) {
		return { label: "Interrupted", tone: "warn" };
	}
	switch (run.status) {
		case "QUEUED":
			return { label: "Starting", tone: "neutral" };
		case "RUNNING":
			return {
				label: `Running ${run.completed} of ${run.total}`,
				tone: "neutral",
			};
		case "CANCELLED":
			return { label: "Stopped", tone: "warn" };
		case "FAILED":
			return { label: "Crashed", tone: "bad" };
		default:
			if (run.stopReason === "cost_cap") {
				return { label: "Hit the spending cap", tone: "warn" };
			}
			return run.failed === 0
				? { label: "All passed", tone: "good" }
				: { label: `${run.failed} failed`, tone: "bad" };
	}
}

/** Badge variant for a tone. */
export const TONE_BADGE = {
	neutral: "secondary",
	good: "success",
	bad: "destructive",
	warn: "warning",
} as const satisfies Record<Tone, string>;

/** "Published v3 with …" → "Published v3". */
export const configName = (label: string) => label.split(" with ")[0] ?? label;

export function passRate(passed: number, failed: number): string {
	const scored = passed + failed;
	return scored ? `${Math.round((passed / scored) * 100)}%` : "–";
}

export function duration(
	start: Date | string | null,
	end: Date | string | null,
): string {
	if (!start) {
		return "–";
	}
	const ms =
		(end ? new Date(end) : new Date()).getTime() -
		new Date(start).getTime();
	const s = Math.max(0, Math.round(ms / 1000));
	return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
}

export const ago = (date: Date | string | null) =>
	date
		? formatDistanceToNowStrict(new Date(date), { addSuffix: true })
		: "never";

export function modelLabel(
	models: EvalsOverview["models"],
	id: string,
): string {
	return models.find((m) => m.id === id)?.label ?? id;
}
